import { supabase } from '@/lib/supabaseClient';

export const VEHICLE_IMAGES_BUCKET = 'vehicle-images';

export function safeStorageFilename(original) {
  const base = (original || 'image.jpg').split(/[/\\]/).pop() || 'image.jpg';
  return base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80) || 'image.jpg';
}

export function buildVehicleImageStoragePath(vehicleId, filename) {
  return `${vehicleId}/${Date.now()}-${safeStorageFilename(filename)}`;
}

/** Extract bucket object path from a public storage URL; null for legacy /uploads paths. */
export function storagePathFromPublicUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const markers = [
    `/storage/v1/object/public/${VEHICLE_IMAGES_BUCKET}/`,
    `/object/public/${VEHICLE_IMAGES_BUCKET}/`,
  ];
  for (const marker of markers) {
    const idx = url.indexOf(marker);
    if (idx !== -1) {
      return decodeURIComponent(url.slice(idx + marker.length).split('?')[0]);
    }
  }
  return null;
}

async function nextSortOrder(vehicleId, category) {
  const { data, error } = await supabase
    .from('vehicle_images')
    .select('sort_order')
    .eq('vehicle_id', vehicleId)
    .eq('category', category);
  if (error) throw error;
  if (!data?.length) return 0;
  return Math.max(...data.map((row) => row.sort_order ?? 0)) + 1;
}

async function demoteMainImages(vehicleId) {
  const { error } = await supabase
    .from('vehicle_images')
    .update({ category: 'other' })
    .eq('vehicle_id', vehicleId)
    .eq('category', 'main');
  if (error) throw error;
}

export async function syncVehicleCoverUrl(vehicleId) {
  const { data, error: fetchError } = await supabase
    .from('vehicle_images')
    .select('url')
    .eq('vehicle_id', vehicleId)
    .eq('category', 'main')
    .order('sort_order')
    .limit(1);
  if (fetchError) throw fetchError;

  const mainUrl = data?.[0]?.url ?? null;
  const { error } = await supabase
    .from('vehicles')
    .update({ image_url: mainUrl, updated_at: new Date().toISOString() })
    .eq('id', vehicleId);
  if (error) throw error;
}

export async function uploadVehicleGalleryImage(vehicleId, file, category) {
  const normalizedCategory = category === 'main' ? 'main' : 'other';
  const path = buildVehicleImageStoragePath(vehicleId, file.name);

  const { error: uploadError } = await supabase.storage
    .from(VEHICLE_IMAGES_BUCKET)
    .upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage.from(VEHICLE_IMAGES_BUCKET).getPublicUrl(path);
  const publicUrl = urlData.publicUrl;

  if (normalizedCategory === 'main') {
    await demoteMainImages(vehicleId);
  }

  const sort_order = await nextSortOrder(vehicleId, normalizedCategory);
  const now = new Date().toISOString();

  const { data: row, error: insertError } = await supabase
    .from('vehicle_images')
    .insert({
      id: crypto.randomUUID(),
      vehicle_id: vehicleId,
      url: publicUrl,
      category: normalizedCategory,
      sort_order,
      created_at: now,
    })
    .select()
    .single();

  if (insertError) {
    await supabase.storage.from(VEHICLE_IMAGES_BUCKET).remove([path]);
    throw insertError;
  }

  if (normalizedCategory === 'main') {
    await syncVehicleCoverUrl(vehicleId);
  }

  return row;
}

export async function deleteVehicleGalleryImage(vehicleId, imageId) {
  const { data: image, error: fetchError } = await supabase
    .from('vehicle_images')
    .select('id, url, category')
    .eq('id', imageId)
    .eq('vehicle_id', vehicleId)
    .single();
  if (fetchError) throw fetchError;

  const storagePath = storagePathFromPublicUrl(image.url);
  if (storagePath) {
    const { error: storageError } = await supabase.storage
      .from(VEHICLE_IMAGES_BUCKET)
      .remove([storagePath]);
    if (storageError) throw storageError;
  }

  const { error: deleteError } = await supabase
    .from('vehicle_images')
    .delete()
    .eq('id', imageId)
    .eq('vehicle_id', vehicleId);
  if (deleteError) throw deleteError;

  if (image.category === 'main') {
    await syncVehicleCoverUrl(vehicleId);
  }
}

export async function setMainVehicleGalleryImage(vehicleId, imageId) {
  const { data: image, error: fetchError } = await supabase
    .from('vehicle_images')
    .select('id, url')
    .eq('id', imageId)
    .eq('vehicle_id', vehicleId)
    .single();
  if (fetchError) throw fetchError;

  await demoteMainImages(vehicleId);

  const { error: updateError } = await supabase
    .from('vehicle_images')
    .update({ category: 'main' })
    .eq('id', imageId)
    .eq('vehicle_id', vehicleId);
  if (updateError) throw updateError;

  const { error: vehicleError } = await supabase
    .from('vehicles')
    .update({
      image_url: image.url,
      updated_at: new Date().toISOString(),
    })
    .eq('id', vehicleId);
  if (vehicleError) throw vehicleError;
}
